// Brand rollup report form (TDC-1499 phase 3). Launched from a Catalog >
// Brands row, so the brand is already chosen — the form picks WHICH of the
// brand's imported products to audit, then enqueues
// POST /rmn/audit/brand-report { brand_id, desireids }. Everything is ticked
// by default, so the plain "report on this brand" path is still one click.
// Options come from GET /rmn/brands/:id/report-targets, which resolves the
// same way the job does: the list shown here IS the list that gets audited.
// The report generates in the background; the page's Brand Reports section
// lists it with a Generating badge and polls.

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import {
    rmnAuditBrandReportEndpoint,
    rmnBrandReportTargetsEndpoint
} from '../../api/endpoints';
import { apiErrorCode } from '../../utils/apiError';
import { RmnBrandReportTarget } from '../../types/rmn';
import { ScoreChip } from '../../pages/network/catalogShared';
import { BORDER, FONT, GOLD, GOLD_DARK, TEXT_FAINT } from '../ProposalFlow/theme';
import { DiscoveredWarning, NothingFoundWarning } from './CategoryAuditForm';
import { useNetworkScope } from '../../hooks/useNetworkScope';

const ERROR = 'var(--down)';

interface Props {
    // Canonical network brand (a GET /rmn/brands row id).
    brandId: number;
    brandName: string;
    // Fired with the new report id after the backend accepts the request.
    onCreated: (id: number) => void;
    // This brand's products still in Discovered (the Brands table's
    // "Discovered" number) — shown as a warning, since they won't be analyzed.
    discoveredCount?: number;
    // Starts the brand shelf analysis (the row's Analyze action) when the
    // brand has nothing imported AND nothing discovered.
    onAnalyze?: () => void;
    analyzeDisabledReason?: string | null;
    // Internal Brands page: no workspace, so the root is passed explicitly
    // (?rmn_brand_id=) on both calls, and an empty brand offers "add a
    // product" instead of the client portal's crawl / Discovered prompts.
    rmnBrandId?: number;
    emptyAction?: { label: string; onClick: () => void };
}

export function BrandReportForm({
    brandId,
    brandName,
    onCreated,
    discoveredCount = 0,
    onAnalyze,
    analyzeDisabledReason,
    rmnBrandId,
    emptyAction,
}: Props) {
    const withRoot = (url: string) =>
        rmnBrandId ? `${url}${url.includes('?') ? '&' : '?'}rmn_brand_id=${rmnBrandId}` : url;
    const { basePath } = useNetworkScope();
    const discoveredHref = `${basePath}/products/discovered?brand=${brandId}`;
    const [targets, setTargets] = useState<RmnBrandReportTarget[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstant.get(withRoot(rmnBrandReportTargetsEndpoint(brandId)));
                if (cancelled) return;
                const rows = (res.data || []) as RmnBrandReportTarget[];
                setTargets(rows);
                setSelected(new Set(rows.map((r) => r.desireid)));
            } catch {
                if (!cancelled) {
                    setLoadError('Couldn\'t load this brand\'s products. Please try again.');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [brandId]);

    const allSelected = useMemo(
        () => !!targets && targets.length > 0 && selected.size === targets.length,
        [targets, selected]
    );

    const toggle = (desireid: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(desireid)) next.delete(desireid);
            else next.add(desireid);
            return next;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || !targets || selected.size === 0) return;
        setSubmitting(true);
        setFormError(null);
        try {
            // Send the picks only when they narrow the run — an untouched
            // form stays the plain "whole brand" request.
            const { data } = await axiosInstant.post(withRoot(rmnAuditBrandReportEndpoint), {
                brand_id: brandId,
                ...(allSelected ? {} : { desireids: [...selected] })
            });
            toast.success(`Brand PDP analysis for ${brandName} started — it will appear in your reports list while it runs.`);
            onCreated(data?.id);
        } catch (err: any) {
            const status = err?.response?.status;
            const code = apiErrorCode(err);
            let message = 'Something went wrong starting the report. Please try again.';
            if (code === 'BrandNotFound') {
                message = 'That brand is no longer available. Refresh the page and try again.';
            } else if (code === 'NotEnoughProductsForBrand') {
                message =
                    'None of these products are in your catalog any more. Refresh the page and try again.';
            } else if (status === 429) {
                message = 'You\'ve reached today\'s limit for advanced reports. Please try again tomorrow.';
            }
            setFormError(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loadError) {
        return <div style={{ fontFamily: FONT, fontSize: 14, color: ERROR }}>{loadError}</div>;
    }

    if (targets === null) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT }}>
                Loading {brandName}&apos;s products…
            </div>
        );
    }

    if (targets.length === 0) {
        if (emptyAction) {
            return (
                <div className="flex flex-col gap-3">
                    <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT, lineHeight: 1.6 }}>
                        {brandName} has no products with a product page to analyze. Add a product with
                        its PDP URL first, then run the analysis.
                    </div>
                    <button
                        type="button"
                        onClick={emptyAction.onClick}
                        className="self-start px-[18px] py-[9px] rounded-[9px] text-[13px] font-semibold text-white bg-accent hover:bg-[#967435] transition-colors"
                    >
                        {emptyAction.label}
                    </button>
                </div>
            );
        }
        if (discoveredCount === 0) {
            return (
                <NothingFoundWarning
                    what={brandName}
                    onAnalyze={onAnalyze}
                    disabledReason={analyzeDisabledReason}
                />
            );
        }
        return (
            <>
                <DiscoveredWarning count={discoveredCount} noun="from this brand are" href={discoveredHref} />
                <div style={{ fontFamily: FONT, fontSize: 14, color: TEXT_FAINT, lineHeight: 1.6 }}>
                    {brandName} has no products in your catalog with a product page to analyze. Add its
                    products from Catalog &rsaquo; Products &rsaquo; Discovered first, then run the analysis.
                </div>
            </>
        );
    }

    return (
        <form onSubmit={handleSubmit} noValidate>
            <DiscoveredWarning count={discoveredCount} noun="from this brand are" href={discoveredHref} />
            <div
                style={{
                    fontFamily: FONT,
                    fontSize: 13,
                    color: TEXT_FAINT,
                    lineHeight: 1.6,
                    marginBottom: 12
                }}
            >
                We&apos;ll audit the product pages you pick and roll the findings into one report.
            </div>

            <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-semibold text-text-1">
                    {selected.size} of {targets.length} selected
                </span>
                <button
                    type="button"
                    onClick={() =>
                        setSelected(allSelected ? new Set() : new Set(targets.map((t) => t.desireid)))
                    }
                    className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2"
                >
                    {allSelected ? 'Select none' : 'Select all'}
                </button>
            </div>

            <div className="flex flex-col border border-border-token rounded-xl overflow-hidden mb-5 max-h-[320px] overflow-y-auto">
                {targets.map((t) => (
                    <label
                        key={t.desireid}
                        className="flex items-center gap-3 px-3 py-2.5 border-b border-border-token last:border-0 cursor-pointer hover:bg-surface-2"
                    >
                        <input
                            type="checkbox"
                            checked={selected.has(t.desireid)}
                            onChange={() => toggle(t.desireid)}
                            className="shrink-0"
                        />
                        <span className="flex-1 min-w-0 flex flex-col">
                            <span className="text-[13px] text-text-1 truncate" title={t.title || t.url}>
                                {t.title || t.url}
                            </span>
                            <span className="text-[11px] text-muted truncate" title={t.url}>
                                {t.url.replace(/^https?:\/\/(www\.)?/, '')}
                            </span>
                        </span>
                        <ScoreChip score={t.latest_score} title="Latest score for this page" />
                    </label>
                ))}
            </div>

            {formError && (
                <div style={{ fontFamily: FONT, fontSize: 13, color: ERROR, marginBottom: 16 }}>
                    {formError}
                </div>
            )}

            <button
                type="submit"
                disabled={submitting || selected.size === 0}
                style={{
                    width: '100%',
                    padding: 14,
                    borderRadius: 12,
                    border: 'none',
                    background: submitting || selected.size === 0 ? BORDER : GOLD,
                    color: submitting || selected.size === 0 ? '#B5AFA5' : '#fff',
                    fontFamily: FONT,
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    cursor: submitting || selected.size === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                    if (!submitting && selected.size > 0)
                        (e.currentTarget as HTMLButtonElement).style.background = GOLD_DARK;
                }}
                onMouseLeave={(e) => {
                    if (!submitting && selected.size > 0)
                        (e.currentTarget as HTMLButtonElement).style.background = GOLD;
                }}
            >
                {submitting ? "Starting analysis…" : "Run Brand PDP Analysis"}
            </button>
            <div style={{ fontFamily: FONT, fontSize: 12, color: TEXT_FAINT, marginTop: 10, textAlign: "center" }}>
                Reports run in the background — expect a few minutes, or longer for brands with
                many products.
            </div>
        </form>
    );
}
